import { TranslationContext } from 'contexts/translation-context';
import styled from 'styled-components';
import { useEffect, useState, useContext } from 'react';
import { Typography, Link, Box, Checkbox, FormControlLabel } from '@mui/material';
import { FormikProps } from 'formik';
import { JoinFormType } from 'types/join';

const TitleText = styled(Typography)(() => ({
  fontWeight: 'bold',
  color: '#000000',
  '@media (max-width: 999px)': {
    fontSize: '9px'
  }
}));

const Text = styled(Typography)(() => ({
  marginBottom: '11px',
  '@media (max-width: 999px)': {
    fontSize: '9px'
  }
}));

const StyledLink = styled(Link)(() => ({
  color: '#9B9B9B',
  textDecoration: 'underline',
  textDecorationColor: '#9B9B9B',
  '&:hover': {
    textDecoration: 'underline',
    textDecorationColor: '#9B9B9B'
  }
}));

const Subscribe = styled.span`
  font-family: ${props => props.theme.fonts.interSans};
  font-size: 14px;
  line-height: 1.45;
  @media (max-width: 999px) {
    font-size: 11px;
  }
  color: #434343;
  display: inline-flex;
  align-items: center;
`;

const CheckboxContainer = styled.div`
  margin-top: 9px;

  & .MuiFormControlLabel-root {
    align-items: center;
    margin-left: 0;
    margin-right: 0;
    gap: 8px;
  }
  & .MuiFormControlLabel-label {
    display: inline-flex;
    align-items: center;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
  && {
    & .MuiFormControlLabel-label {
      font-family: 'Inter', 'Montserrat-Regular', sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
    }
    color: ${props => props.theme.colors.siteIndigo};
    &.Mui-checked {
      color: ${props => props.theme.colors.siteIndigo};
    }
  }
`;
const CheckboxBox = styled.span`
  width: 16px;
  height: 16px;
  border: 1.5px solid ${props => props.theme.colors.siteIndigo};
  border-radius: 3px;
  background-color: ${props => props.theme.colors.white};
  box-sizing: border-box;
  display: inline-block;
`;
const CheckboxCheckedBox = styled(CheckboxBox)`
  background-color: ${props => props.theme.colors.siteIndigo};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid ${props => props.theme.colors.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const StyleToggle = styled.span`
  color: ${props => props.theme.colors.siteIndigo};
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
  @media (max-width: 999px) {
    font-size: 11px;
  }
  white-space: nowrap;
  font-family: 'Inter', 'Montserrat-Regular', sans-serif;
  margin-left: 0;
  display: inline-flex;
  align-items: center;
  margin-top: -4px;
  &:hover {
    text-decoration: underline;
  }
`;

export const SmsTermsMoreDetailsContent = ({
  form,
  disabled = false
}: {
  form: FormikProps<JoinFormType>;
  disabled: boolean;
}) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  const toggleMoreDetails = () => {
    setShowMoreDetails(!showMoreDetails);
  };

  return (
    <CheckboxContainer>
      <Row>
        <FormControlLabel
          label={<Subscribe>{TRANSLATION_TEXT.agree_to_receive_texts_per_the_sms_terms}</Subscribe>}
          control={
            <StyledCheckbox
              disabled={disabled}
              checked={form.values.smsTerms}
              onChange={() => {
                form.setFieldValue('smsTerms', !form.values.smsTerms);
              }}
              color="primary"
              icon={<CheckboxBox />}
              checkedIcon={<CheckboxCheckedBox />}
            />
          }
        />
        <StyleToggle onClick={toggleMoreDetails}>
          {showMoreDetails ? 'Less Details' : 'More Details'}
        </StyleToggle>
      </Row>
      {showMoreDetails && (
        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3, color: '#9B9B9B' }}>
          <TitleText variant="h6">{TRANSLATION_TEXT.program_description_title}</TitleText>
          <Text variant="body2" paragraph>
            {TRANSLATION_TEXT.program_description}
          </Text>

          <TitleText variant="h6">{TRANSLATION_TEXT.cost}</TitleText>
          <Text variant="body2" paragraph>
            {TRANSLATION_TEXT.cost_description}
          </Text>

          <TitleText variant="h6">{TRANSLATION_TEXT.message_frequency_title}</TitleText>
          <Text variant="body2" paragraph>
            {TRANSLATION_TEXT.message_frequency_description}
          </Text>

          <TitleText variant="h6">{TRANSLATION_TEXT.how_to_opt_out_title}</TitleText>
          <Text variant="body2" paragraph>
            {TRANSLATION_TEXT.how_to_opt_out_description}
          </Text>

          <TitleText variant="h6">{TRANSLATION_TEXT.support_or_help_title}</TitleText>
          <Text variant="body2">
            {TRANSLATION_TEXT.support_or_help_description}{' '}
            <StyledLink
              href="https://www.rapnet.com/contact/support/"
              underline="always"
              target="_blank"
              rel="noopener noreferrer"
            >
              {TRANSLATION_TEXT.contact_us}
            </StyledLink>
            .
          </Text>
        </Box>
      )}
    </CheckboxContainer>
  );
};

export default SmsTermsMoreDetailsContent;
